import { APITypesV1 } from "@cur8/api-client";
import { labProgressFor } from "lib/tubeStatus";
import { CheckInParams } from "lib/types/checkInParameters";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { useVisitBloodSamplesQuery } from "./useVisitBloodSamplesQuery";

// The lab moves a tube on minutes after it leaves the room, and the point is
// that the operator can still act, so the screens showing lab status poll while
// the flag is on. One place, so the two can't drift apart (AB#92441).
const LAB_STATUS_POLL_MS = 5000;

// Nothing the lab can still move: every tube it holds has a verdict, or the
// visit has no lab side at all. The lab doesn't reopen a verdict, so an
// accepted or rejected tube has nothing left to report. A tube with no status
// yet keeps the poll alive, since its status is still to come. Read off the
// fetched samples rather than the render, so an error with no data polls on
// instead of giving up.
function labStatusSettled(
  data: APITypesV1.BloodSamplesResponse | undefined
): boolean {
  if (data == null) {
    return false;
  }
  const progress = labProgressFor(data.bloodSamples);
  return progress == null || progress === "labFinished";
}

export function useLabStatusBloodSamplesQuery({
  patientId,
  visitId,
}: CheckInParams) {
  const { data: featurePreferences } = useFeaturePreference();
  const labStatusEnabled = featurePreferences.bloodLabStatusEnabled;

  // One request per visit, not per tube, and it stops once every tube has
  // settled. React Query re-reads this after each fetch, so a tube that turns
  // up still in flight starts the poll again by itself.
  const query = useVisitBloodSamplesQuery({
    patientId,
    visitId,
    options: {
      refetchInterval: ({ state }) =>
        labStatusEnabled && !labStatusSettled(state.data)
          ? LAB_STATUS_POLL_MS
          : false,
    },
  });

  return { labStatusEnabled, query };
}
